import React, { useState, useEffect, useCallback } from 'react'
import {
  FileText,
  Users,
  Shield,
  Zap,
  Plus,
  Search,
  Copy,
  Archive,
  RefreshCw,
  Edit2,
  CheckCircle2,
  AlertCircle,
  X,
  History,
  Eye,
  Clock,
  Radio,
  BarChart2,
} from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { sportsService } from './sportsService'
import { fieldCaptureService } from './fieldCaptureService'
import {
  PracticeScript,
  LineupTemplate,
  OffensivePlay,
  DefensiveCall,
  OffensiveConcept,
  RosterAthleteOption,
  LineupSlot,
} from '../../types/sports'
import { ScriptEditorModal } from './ScriptEditorModal'
import { LineupEditorModal } from './LineupEditorModal'
import { FieldCaptureEntryModal } from './FieldCaptureEntryModal'
import { FieldConsoleView } from './FieldConsoleView'
import { SnapHistoryModal } from './SnapHistoryModal'
import { PastSnapDetailModal } from './PastSnapDetailModal'
import { Modal } from '../shared/Modal'
import { TeamBlockCapture, CaptureWithSnapCount, FieldSnap } from '../../types/fieldCapture'
import { StatsView } from './StatsView'
import { SectionErrorBoundary } from '../shared/SectionErrorBoundary'

export const SportsView: React.FC = () => {
  const { user, hasCapability, hasRole, isMedic } = useAuth()
  const teamId =
    user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

  const canManagePlaybook = hasCapability('manage_playbook', teamId)
  const canCapture = hasCapability('capture_field', teamId)
  const canStats = hasCapability('view_stats', teamId)
  const isCoachUser = canManagePlaybook || canCapture || canStats
  const isAthleteUser = hasRole('athlete', teamId)
  const isMedicUser = isMedic(teamId)

  // Pestaña activa con soporte para query params (?sportsTab=... o ?tab=...)
  const [activeTab, setActiveTab] = useState<'scripts' | 'lineups' | 'offense' | 'defense' | 'captures' | 'stats'>(() => {
    const params = new URLSearchParams(window.location.search)
    const t = params.get('sportsTab') || params.get('tab')
    if (isCoachUser) {
      if ((t === 'stats' && canStats) || (t === 'captures' && (canCapture || canStats)) || (canManagePlaybook && (t === 'lineups' || t === 'offense' || t === 'defense' || t === 'scripts'))) {
        return t
      }
      return canManagePlaybook ? 'scripts' : canCapture ? 'captures' : 'stats'
    }
    return 'stats'
  })

  const handleTabChange = (newTab: 'scripts' | 'lineups' | 'offense' | 'defense' | 'captures' | 'stats') => {
    if (newTab === 'stats' && !canStats && !isAthleteUser) return
    if (newTab === 'captures' && !canCapture && !canStats) return
    if (!['stats', 'captures'].includes(newTab) && !canManagePlaybook) return
    setActiveTab(newTab)
    const url = new URL(window.location.href)
    url.searchParams.set('sportsTab', newTab)
    window.history.replaceState({}, '', url.toString())
  }

  // Datos
  const [scripts, setScripts] = useState<PracticeScript[]>([])
  const [lineups, setLineups] = useState<LineupTemplate[]>([])
  const [offensivePlays, setOffensivePlays] = useState<OffensivePlay[]>([])
  const [offensiveConcepts, setOffensiveConcepts] = useState<OffensiveConcept[]>([])
  const [defensiveCalls, setDefensiveCalls] = useState<DefensiveCall[]>([])
  const [athletes, setAthletes] = useState<RosterAthleteOption[]>([])
  const [allCaptures, setAllCaptures] = useState<CaptureWithSnapCount[]>([])

  // Estado para visor de historial snap a snap en modo consulta
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedHistoryCapture, setSelectedHistoryCapture] = useState<TeamBlockCapture | null>(null)
  const [historySnaps, setHistorySnaps] = useState<FieldSnap[]>([])
  const [pastSnapDetailOpen, setPastSnapDetailOpen] = useState(false)
  const [selectedDetailSnap, setSelectedDetailSnap] = useState<FieldSnap | null>(null)
  const [loadingHistoryId, setLoadingHistoryId] = useState<string | null>(null)

  // Filtros y búsquedas
  const [searchQuery, setSearchQuery] = useState('')
  const [conceptFilter, setConceptFilter] = useState('ALL')
  const [lineupUnitFilter, setLineupUnitFilter] = useState<'ALL' | 'offense' | 'defense'>('ALL')
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active')

  // Estados de carga y feedback
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Modales
  const [scriptModalOpen, setScriptModalOpen] = useState(false)
  const [selectedScriptId, setSelectedScriptId] = useState<string | null>(null)
  const [captureEntryModalOpen, setCaptureEntryModalOpen] = useState(false)
  const [entryScriptId, setEntryScriptId] = useState<string | null>(null)
  const [activeCaptureId, setActiveCaptureId] = useState<string | null>(null)

  const [lineupModalState, setLineupModalState] = useState<{
    isOpen: boolean
    templateId?: string
    unit: 'offense' | 'defense'
    initialName?: string
    initialSlots?: LineupSlot[]
    sessionKey?: number
  }>({
    isOpen: false,
    unit: 'offense',
    sessionKey: 0,
  })

  // Modal para crear/editar jugada
  const [playModalOpen, setPlayModalOpen] = useState(false)
  const [editingPlay, setEditingPlay] = useState<OffensivePlay | null>(null)
  const [playFormData, setPlayFormData] = useState({
    name: '',
    shortCode: '',
    conceptId: '',
    description: '',
    coachingNotes: '',
  })

  // Modal para crear/editar llamada defensiva
  const [callModalOpen, setCallModalOpen] = useState(false)
  const [editingCall, setEditingCall] = useState<DefensiveCall | null>(null)
  const [callFormData, setCallFormData] = useState({
    name: '',
    shortCode: '',
    coverageFamily: '',
    description: '',
    coachingNotes: '',
  })

  // Detección de cambios sin guardar en jugadas y llamadas
  const isPlayDirty = editingPlay
    ? playFormData.name !== editingPlay.name ||
      playFormData.shortCode !== editingPlay.short_code ||
      playFormData.conceptId !== editingPlay.concept_id ||
      playFormData.description !== (editingPlay.description || '') ||
      playFormData.coachingNotes !== (editingPlay.coaching_notes || '')
    : Boolean(
        playFormData.name.trim() ||
        playFormData.shortCode.trim() ||
        playFormData.description.trim() ||
        playFormData.coachingNotes.trim()
      )

  const isCallDirty = editingCall
    ? callFormData.name !== editingCall.name ||
      callFormData.shortCode !== editingCall.short_code ||
      callFormData.coverageFamily !== editingCall.coverage_family ||
      callFormData.description !== (editingCall.description || '') ||
      callFormData.coachingNotes !== (editingCall.coaching_notes || '')
    : Boolean(
        callFormData.name.trim() ||
        callFormData.shortCode.trim() ||
        callFormData.coverageFamily.trim() ||
        callFormData.description.trim() ||
        callFormData.coachingNotes.trim()
      )

  // Carga de datos
  const loadData = useCallback(async () => {
    if (!teamId || !isCoachUser) return
    setLoading(true)
    setErrorMsg(null)
    try {
      const [
        scriptsRes,
        lineupsRes,
        playsRes,
        conceptsRes,
        callsRes,
        athletesRes,
        capturesRes,
      ] = await Promise.all([
        sportsService.getPracticeScripts(teamId),
        sportsService.getLineupTemplates(teamId, { status: statusFilter }),
        sportsService.getOffensivePlays(teamId, { status: statusFilter }),
        sportsService.getOffensiveConcepts(teamId, 'all'),
        sportsService.getDefensiveCalls(teamId, { status: statusFilter }),
        sportsService.getTeamAthletesWithEventContext(teamId),
        fieldCaptureService.getAllCapturesWithSnapCount(teamId).catch(() => []),
      ])

      setScripts(scriptsRes)
      setLineups(lineupsRes)
      setOffensivePlays(playsRes)
      setOffensiveConcepts(conceptsRes)
      setDefensiveCalls(callsRes)
      setAthletes(athletesRes)
      setAllCaptures(capturesRes)
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message || 'Error al cargar datos deportivos')
    } finally {
      setLoading(false)
    }
  }, [teamId, isCoachUser, statusFilter])

  // Abrir historial de una captura en modo consulta (sin crear ni reabrir captura)
  const handleOpenHistory = async (cap: TeamBlockCapture) => {
    setLoadingHistoryId(cap.id)
    setErrorMsg(null)
    try {
      const details = await fieldCaptureService.getCaptureDetails(cap.id)
      setSelectedHistoryCapture(details.capture)
      setHistorySnaps(details.snaps)
      setHistoryModalOpen(true)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar historial de jugadas.')
    } finally {
      setLoadingHistoryId(null)
    }
  }

  useEffect(() => {
    loadData()
  }, [loadData])

  // Mostrar mensaje de éxito temporal
  const triggerSuccess = (msg: string) => {
    setSuccessMsg(msg)
    setTimeout(() => setSuccessMsg(null), 3000)
  }

  // Permisos: solo coach deportivo autorizado (no concedido implícitamente a admin puro ni sanitario)
  const canEdit = canManagePlaybook

  // --------------------------------------------------------------------------
  // HANDLERS DE QUINTETOS
  // --------------------------------------------------------------------------
  const handleDuplicateLineup = async (templateId: string, currentName: string) => {
    try {
      await sportsService.duplicateLineupTemplate(templateId, `${currentName} (Copia)`)
      triggerSuccess('Quinteto duplicado exitosamente.')
      loadData()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message)
    }
  }

  const handleArchiveLineup = async (templateId: string) => {
    try {
      await sportsService.archiveLineupTemplate(templateId)
      triggerSuccess('Quinteto archivado exitosamente.')
      loadData()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message)
    }
  }

  // --------------------------------------------------------------------------
  // HANDLERS DE JUGADAS OFENSIVAS
  // --------------------------------------------------------------------------
  const openCreatePlayModal = () => {
    setEditingPlay(null)
    setPlayFormData({
      name: '',
      shortCode: '',
      conceptId: offensiveConcepts[0]?.id || '',
      description: '',
      coachingNotes: '',
    })
    setPlayModalOpen(true)
  }

  const openEditPlayModal = (play: OffensivePlay) => {
    setEditingPlay(play)
    setPlayFormData({
      name: play.name,
      shortCode: play.short_code,
      conceptId: play.concept_id,
      description: play.description || '',
      coachingNotes: play.coaching_notes || '',
    })
    setPlayModalOpen(true)
  }

  const handleSavePlay = async () => {
    if (!playFormData.name.trim() || !playFormData.shortCode.trim() || !playFormData.conceptId) {
      setErrorMsg('Nombre, código corto y concepto son obligatorios.')
      return
    }

    try {
      if (editingPlay) {
        await sportsService.updateOffensivePlay(editingPlay.id, {
          name: playFormData.name,
          shortCode: playFormData.shortCode,
          conceptId: playFormData.conceptId,
          description: playFormData.description,
          coachingNotes: playFormData.coachingNotes,
        })
        triggerSuccess('Jugada actualizada exitosamente.')
      } else {
        await sportsService.createOffensivePlay({
          teamId,
          name: playFormData.name,
          shortCode: playFormData.shortCode,
          conceptId: playFormData.conceptId,
          description: playFormData.description,
          coachingNotes: playFormData.coachingNotes,
        })
        triggerSuccess('Jugada creada exitosamente.')
      }
      setPlayModalOpen(false)
      loadData()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message)
    }
  }

  const handleTogglePlayStatus = async (play: OffensivePlay) => {
    const nextStatus = play.status === 'active' ? 'archived' : 'active'
    try {
      await sportsService.updateOffensivePlay(play.id, { status: nextStatus })
      triggerSuccess(`Jugada ${nextStatus === 'archived' ? 'archivada' : 'restaurada'}.`)
      loadData()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message)
    }
  }

  // --------------------------------------------------------------------------
  // HANDLERS DE LLAMADAS DEFENSIVAS
  // --------------------------------------------------------------------------
  const openCreateCallModal = () => {
    setEditingCall(null)
    setCallFormData({
      name: '',
      shortCode: '',
      coverageFamily: '',
      description: '',
      coachingNotes: '',
    })
    setCallModalOpen(true)
  }

  const openEditCallModal = (call: DefensiveCall) => {
    setEditingCall(call)
    setCallFormData({
      name: call.name,
      shortCode: call.short_code,
      coverageFamily: call.coverage_family || '',
      description: call.description || '',
      coachingNotes: call.coaching_notes || '',
    })
    setCallModalOpen(true)
  }

  const handleSaveCall = async () => {
    if (!callFormData.name.trim() || !callFormData.shortCode.trim()) {
      setErrorMsg('Nombre y código corto son obligatorios.')
      return
    }

    try {
      if (editingCall) {
        await sportsService.updateDefensiveCall(editingCall.id, {
          name: callFormData.name,
          shortCode: callFormData.shortCode,
          coverageFamily: callFormData.coverageFamily,
          description: callFormData.description,
          coachingNotes: callFormData.coachingNotes,
        })
        triggerSuccess('Llamada defensiva actualizada.')
      } else {
        await sportsService.createDefensiveCall({
          teamId,
          name: callFormData.name,
          shortCode: callFormData.shortCode,
          coverageFamily: callFormData.coverageFamily,
          description: callFormData.description,
          coachingNotes: callFormData.coachingNotes,
        })
        triggerSuccess('Llamada defensiva creada.')
      }
      setCallModalOpen(false)
      loadData()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message)
    }
  }

  const handleToggleCallStatus = async (call: DefensiveCall) => {
    const nextStatus = call.status === 'active' ? 'archived' : 'active'
    try {
      await sportsService.updateDefensiveCall(call.id, { status: nextStatus })
      triggerSuccess(`Llamada defensiva ${nextStatus === 'archived' ? 'archivada' : 'restaurada'}.`)
      loadData()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message)
    }
  }

  // --------------------------------------------------------------------------
  // FILTRADO LOCAL
  // --------------------------------------------------------------------------
  const filteredPlays = offensivePlays.filter((p) => {
    if (conceptFilter !== 'ALL' && p.concept_id !== conceptFilter) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return (
        p.name.toLowerCase().includes(q) ||
        p.short_code.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q))
      )
    }
    return true
  })

  const filteredCalls = defensiveCalls.filter((c) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return (
        c.name.toLowerCase().includes(q) ||
        c.short_code.toLowerCase().includes(q) ||
        (c.coverage_family && c.coverage_family.toLowerCase().includes(q))
      )
    }
    return true
  })

  const filteredLineups = lineups.filter((l) => {
    if (lineupUnitFilter !== 'ALL' && l.unit !== lineupUnitFilter) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return l.name.toLowerCase().includes(q)
    }
    return true
  })

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '3rem' }}>
      {/* Cabecera */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1.25rem',
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.25rem 0' }}>
            Planificación Deportiva y Catálogos
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
            Guiones de bloques Team, quintetos reutilizables, conceptos ofensivos y llamadas defensivas.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {canCapture && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setEntryScriptId(null)
                setCaptureEntryModalOpen(true)
              }}
              style={{
                padding: '0.45rem 0.85rem',
                fontSize: '0.82rem',
                backgroundColor: 'var(--brand-red)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}
              title="Abrir consola móvil de captura en campo"
            >
              <Radio size={14} />
              <span>Captura en Campo</span>
            </button>
          )}
          <button
            onClick={loadData}
            className="btn btn-outline"
            style={{ padding: '0.4rem 0.65rem', fontSize: '0.8rem' }}
            title="Refrescar datos"
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Alertas */}
      {errorMsg && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'rgba(217, 35, 44, 0.08)',
            border: '1px solid rgba(217, 35, 44, 0.25)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--brand-red)',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <AlertCircle size={16} />
          <span>{errorMsg}</span>
          <button
            onClick={() => setErrorMsg(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {successMsg && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            borderRadius: 'var(--radius-md)',
            color: '#059669',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <CheckCircle2 size={16} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Pestañas Principales - Navegación responsiva adaptada a móvil y escritorio (BK-09) */}
      <div className="sports-nav-tabs">
        {isCoachUser ? (
          <>
            {canManagePlaybook && <button
              id="tab-scripts"
              type="button"
              onClick={() => handleTabChange('scripts')}
              className={`sports-nav-btn ${activeTab === 'scripts' ? 'active' : ''}`}
            >
              <FileText size={16} />
              <span>Guiones Team</span>
              <span className="badge badge-muted" style={{ fontSize: '0.7rem' }}>
                {scripts.length}
              </span>
            </button>}

            {canManagePlaybook && <button
              id="tab-lineups"
              type="button"
              onClick={() => handleTabChange('lineups')}
              className={`sports-nav-btn ${activeTab === 'lineups' ? 'active' : ''}`}
            >
              <Users size={16} />
              <span>Quintetos Reutilizables</span>
              <span className="badge badge-muted" style={{ fontSize: '0.7rem' }}>
                {lineups.length}
              </span>
            </button>}

            {canManagePlaybook && <button
              id="tab-offense"
              type="button"
              onClick={() => handleTabChange('offense')}
              className={`sports-nav-btn ${activeTab === 'offense' ? 'active' : ''}`}
            >
              <Zap size={16} />
              <span>Catálogo Ofensivo</span>
              <span className="badge badge-muted" style={{ fontSize: '0.7rem' }}>
                {offensivePlays.length}
              </span>
            </button>}

            {canManagePlaybook && <button
              id="tab-defense"
              type="button"
              onClick={() => handleTabChange('defense')}
              className={`sports-nav-btn ${activeTab === 'defense' ? 'active' : ''}`}
            >
              <Shield size={16} />
              <span>Catálogo Defensivo</span>
              <span className="badge badge-muted" style={{ fontSize: '0.7rem' }}>
                {defensiveCalls.length}
              </span>
            </button>}

            {(canCapture || canStats) && <button
              id="tab-captures"
              type="button"
              onClick={() => handleTabChange('captures')}
              className={`sports-nav-btn ${activeTab === 'captures' ? 'active' : ''}`}
            >
              <History size={16} />
              <span>Historial de capturas</span>
              <span className="badge badge-muted" style={{ fontSize: '0.7rem' }}>
                {allCaptures.length}
              </span>
            </button>}

            {(canStats || isAthleteUser) && <button
              id="tab-stats"
              type="button"
              onClick={() => handleTabChange('stats')}
              className={`sports-nav-btn ${activeTab === 'stats' ? 'active' : ''}`}
            >
              <BarChart2 size={16} />
              <span>Estadísticas</span>
            </button>}
          </>
        ) : isAthleteUser ? (
          <>
            {(canStats || isAthleteUser) && <button
              id="tab-stats"
              type="button"
              onClick={() => handleTabChange('stats')}
              className={`sports-nav-btn ${activeTab === 'stats' ? 'active' : ''}`}
            >
              <BarChart2 size={16} />
              <span>Mis Estadísticas y Rendimiento</span>
            </button>}
          </>
        ) : null}
      </div>

      {/* Vista informativa para rol sanitario: datos tácticos reservados */}
      {isMedicUser && !isCoachUser && (
        <div
          className="card"
          style={{
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--radius-lg)',
            margin: '1.5rem 0',
          }}
        >
          <Shield size={36} style={{ color: 'var(--brand-blue)', marginBottom: '0.75rem' }} />
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>
            Acceso Deportivo Restringido al Personal Sanitario
          </h3>
          <p style={{ margin: '0 auto 1rem auto', maxWidth: '540px', fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Por diseño de aislamiento y confidencialidad táctica, el registro de jugadas, tácticas y métricas deportivas está reservado al cuerpo técnico y atletas participantes.
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Tu área de trabajo operativo es <strong>Formularios y Salud</strong>, donde gestionas cuestionarios médicos, registros de bienestar y disponibilidad física.
          </p>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 1: GUIONES DE ENTRENAMIENTO */}
      {/* ==================================================================== */}
      {canManagePlaybook && activeTab === 'scripts' && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Guiones preparados para bloques Team (práctica guiada o competitivo)
              </span>
            </div>

            {canEdit && (
              <button
                onClick={() => {
                  setSelectedScriptId(null)
                  setScriptModalOpen(true)
                }}
                className="btn btn-primary"
                style={{ fontSize: '0.85rem' }}
              >
                <Plus size={15} style={{ marginRight: '0.35rem' }} /> Nuevo Guion Team
              </button>
            )}
          </div>

          {scripts.length === 0 ? (
            <div
              className="table-container"
              style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <FileText size={32} style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }} />
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>No hay guiones de práctica creados</p>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem' }}>
                Crea un nuevo guion para planificar secuencias de repeticiones de ataque vs defensa.
              </p>
              {canEdit && (
                <button
                  onClick={() => {
                    setSelectedScriptId(null)
                    setScriptModalOpen(true)
                  }}
                  className="btn btn-primary"
                  style={{ fontSize: '0.85rem' }}
                >
                  <Plus size={15} style={{ marginRight: '0.25rem' }} /> Crear Guion
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
              {scripts.map((s) => (
                <div
                  key={s.id}
                  style={{
                    backgroundColor: 'var(--surface-card)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1rem 1.25rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{s.name}</h3>
                      <span className={`badge ${s.status === 'ready' ? 'badge-green' : 'badge-yellow'}`}>
                        {s.status === 'ready' ? 'Listo' : 'Borrador'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <span className="badge badge-muted" style={{ fontSize: '0.75rem' }}>
                        {s.mode === 'competitive_team' ? 'Team Competitivo' : 'Práctica Guiada'}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        <strong>{s.reps_count || 0}</strong> repeticiones
                      </span>
                    </div>

                    {s.objective && (
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem 0' }}>
                        {s.objective}
                      </p>
                    )}
                  </div>

                  <div
                    style={{
                      borderTop: '1px solid var(--surface-border)',
                      paddingTop: '0.75rem',
                      marginTop: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '0.5rem',
                    }}
                  >
                    {canCapture && (
                      <button
                        type="button"
                        onClick={() => {
                          setEntryScriptId(s.id)
                          setCaptureEntryModalOpen(true)
                        }}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.8rem', padding: '0.35rem 0.65rem' }}
                        title="Iniciar captura en campo con este guion"
                      >
                        <Radio size={13} style={{ marginRight: '0.25rem' }} /> Iniciar Captura
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedScriptId(s.id)
                        setScriptModalOpen(true)
                      }}
                      className="btn btn-primary"
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.65rem' }}
                    >
                      <Edit2 size={13} style={{ marginRight: '0.25rem' }} /> Editar Guion
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 2: QUINTETOS REUTILIZABLES */}
      {/* ==================================================================== */}
      {canManagePlaybook && activeTab === 'lineups' && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            {/* Filtro por unidad */}
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <button
                onClick={() => setLineupUnitFilter('ALL')}
                className={`btn ${lineupUnitFilter === 'ALL' ? 'btn-primary' : 'btn-outline'}`}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
              >
                Todos ({lineups.length})
              </button>
              <button
                onClick={() => setLineupUnitFilter('offense')}
                className={`btn ${lineupUnitFilter === 'offense' ? 'btn-primary' : 'btn-outline'}`}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
              >
                Ataque ({lineups.filter((l) => l.unit === 'offense').length})
              </button>
              <button
                onClick={() => setLineupUnitFilter('defense')}
                className={`btn ${lineupUnitFilter === 'defense' ? 'btn-primary' : 'btn-outline'}`}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
              >
                Defensa ({lineups.filter((l) => l.unit === 'defense').length})
              </button>
            </div>

            {canEdit && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() =>
                    setLineupModalState({
                      isOpen: true,
                      unit: 'offense',
                      initialName: 'Ataque Nuevo',
                      sessionKey: Date.now(),
                    })
                  }
                  className="btn btn-outline"
                  style={{ fontSize: '0.82rem' }}
                >
                  <Plus size={14} style={{ marginRight: '0.25rem' }} /> + Quinteto Ataque
                </button>
                <button
                  onClick={() =>
                    setLineupModalState({
                      isOpen: true,
                      unit: 'defense',
                      initialName: 'Defensa Nueva',
                      sessionKey: Date.now(),
                    })
                  }
                  className="btn btn-outline"
                  style={{ fontSize: '0.82rem' }}
                >
                  <Plus size={14} style={{ marginRight: '0.25rem' }} /> + Quinteto Defensa
                </button>
              </div>
            )}
          </div>

          {filteredLineups.length === 0 ? (
            <div className="table-container" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No hay quintetos que coincidan con el filtro seleccionado.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1rem' }}>
              {filteredLineups.map((lineup) => (
                <div
                  key={lineup.id}
                  style={{
                    backgroundColor: 'var(--surface-card)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: lineup.unit === 'offense' ? 'rgba(217, 35, 44, 0.1)' : 'rgba(37, 99, 235, 0.1)',
                          color: lineup.unit === 'offense' ? 'var(--brand-red)' : '#2563eb',
                          fontWeight: 700,
                        }}
                      >
                        {lineup.unit === 'offense' ? 'ATAQUE' : 'DEFENSA'}
                      </span>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>{lineup.name}</h3>
                    </div>

                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      {canEdit && (
                        <>
                          <button
                            onClick={() =>
                              setLineupModalState({
                                isOpen: true,
                                templateId: lineup.id,
                                unit: lineup.unit,
                                initialName: lineup.name,
                                initialSlots: lineup.slots.map((s) => ({
                                  athlete_id: s.athlete_id,
                                  position: s.position,
                                  athlete_name: s.athlete ? `${s.athlete.first_name} ${s.athlete.last_name}` : 'Atleta',
                                  dorsal_number: s.athlete?.dorsal_number,
                                  primary_position: s.athlete?.primary_position,
                                })),
                                sessionKey: Date.now(),
                              })
                            }
                            className="btn-icon"
                            title="Editar quinteto"
                            aria-label={`Editar quinteto ${lineup.name}`}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDuplicateLineup(lineup.id, lineup.name)}
                            className="btn-icon"
                            title="Duplicar quinteto"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => handleArchiveLineup(lineup.id)}
                            className="btn-icon"
                            title="Archivar quinteto"
                          >
                            <Archive size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* 5 Slots */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {lineup.slots.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.35rem 0.5rem',
                          backgroundColor: 'var(--surface-secondary)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.8rem',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            color: lineup.unit === 'offense' ? 'var(--brand-red)' : '#2563eb',
                            width: '55px',
                          }}
                        >
                          {s.position}
                        </span>
                        <span style={{ flex: 1, fontWeight: 500 }}>
                          {s.athlete ? `${s.athlete.first_name} ${s.athlete.last_name}` : 'Atleta'}
                        </span>
                        <span className="badge badge-muted" style={{ fontSize: '0.7rem' }}>
                          {s.athlete?.dorsal_number ? `#${s.athlete.dorsal_number} · ` : ''}
                          {s.athlete?.primary_position || 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 3: CATÁLOGO OFENSIVO */}
      {/* ==================================================================== */}
      {canManagePlaybook && activeTab === 'offense' && (
        <div>
          {/* Barra de Filtros */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', width: '220px' }}>
                <Search size={14} style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder="Buscar jugada o código..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.4rem 0.65rem 0.4rem 2rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--surface-border)',
                    fontSize: '0.82rem',
                  }}
                />
              </div>

              <select
                value={conceptFilter}
                onChange={(e) => setConceptFilter(e.target.value)}
                style={{ padding: '0.4rem 0.65rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)', fontSize: '0.82rem' }}
              >
                <option value="ALL">Todos los conceptos</option>
                {offensiveConcepts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'active' | 'archived' | 'all')}
                style={{ padding: '0.4rem 0.65rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)', fontSize: '0.82rem' }}
              >
                <option value="active">Activas</option>
                <option value="archived">Archivadas</option>
                <option value="all">Todas</option>
              </select>
            </div>

            {canEdit && (
              <button onClick={openCreatePlayModal} className="btn btn-primary" style={{ fontSize: '0.85rem' }}>
                <Plus size={15} style={{ marginRight: '0.35rem' }} /> Nueva Jugada Ofensiva
              </button>
            )}
          </div>

          {/* Tabla de Jugadas */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>Código</th>
                  <th style={{ width: '160px' }}>Nombre</th>
                  <th style={{ width: '120px' }}>Concepto</th>
                  <th>Descripción e Indicaciones</th>
                  <th style={{ width: '90px' }}>Estado</th>
                  {canEdit && <th style={{ width: '100px', textAlign: 'right' }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filteredPlays.length === 0 ? (
                  <tr>
                    <td colSpan={canEdit ? 6 : 5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No se encontraron jugadas con los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filteredPlays.map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 700, color: 'var(--brand-red)' }}>[{p.short_code}]</td>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>
                        <span className="badge badge-muted">{p.concept?.name || 'Base'}</span>
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        {p.description && <div>{p.description}</div>}
                        {p.coaching_notes && (
                          <div style={{ color: 'var(--brand-red)', marginTop: '2px', fontSize: '0.78rem' }}>
                            Ajustes: {p.coaching_notes}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${p.status === 'active' ? 'badge-green' : 'badge-muted'}`}>
                          {p.status === 'active' ? 'Activa' : 'Archivada'}
                        </span>
                      </td>
                      {canEdit && (
                        <td style={{ textAlign: 'right' }}>
                          <button
                            onClick={() => openEditPlayModal(p)}
                            className="btn-icon"
                            title="Editar jugada"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleTogglePlayStatus(p)}
                            className="btn-icon"
                            title={p.status === 'active' ? 'Archivar jugada' : 'Restaurar jugada'}
                          >
                            <Archive size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 4: CATÁLOGO DEFENSIVO */}
      {/* ==================================================================== */}
      {canManagePlaybook && activeTab === 'defense' && (
        <div>
          {/* Barra de Filtros */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', width: '220px' }}>
                <Search size={14} style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder="Buscar llamada o cobertura..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.4rem 0.65rem 0.4rem 2rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--surface-border)',
                    fontSize: '0.82rem',
                  }}
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'active' | 'archived' | 'all')}
                style={{ padding: '0.4rem 0.65rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)', fontSize: '0.82rem' }}
              >
                <option value="active">Activas</option>
                <option value="archived">Archivadas</option>
                <option value="all">Todas</option>
              </select>
            </div>

            {canEdit && (
              <button onClick={openCreateCallModal} className="btn btn-primary" style={{ fontSize: '0.85rem' }}>
                <Plus size={15} style={{ marginRight: '0.35rem' }} /> Nueva Llamada Defensiva
              </button>
            )}
          </div>

          {/* Tabla de Llamadas Defensivas */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>Código</th>
                  <th style={{ width: '180px' }}>Nombre</th>
                  <th style={{ width: '130px' }}>Cobertura / Familia</th>
                  <th>Descripción y Ajustes de Staff</th>
                  <th style={{ width: '90px' }}>Estado</th>
                  {canEdit && <th style={{ width: '100px', textAlign: 'right' }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filteredCalls.length === 0 ? (
                  <tr>
                    <td colSpan={canEdit ? 6 : 5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No se encontraron llamadas defensivas con los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filteredCalls.map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 700, color: '#2563eb' }}>[{c.short_code}]</td>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td>
                        <span className="badge badge-muted">{c.coverage_family || 'Sin familia'}</span>
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        {c.description && <div>{c.description}</div>}
                        {c.coaching_notes && (
                          <div style={{ color: '#2563eb', marginTop: '2px', fontSize: '0.78rem' }}>
                            Ajustes: {c.coaching_notes}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${c.status === 'active' ? 'badge-green' : 'badge-muted'}`}>
                          {c.status === 'active' ? 'Activa' : 'Archivada'}
                        </span>
                      </td>
                      {canEdit && (
                        <td style={{ textAlign: 'right' }}>
                          <button
                            onClick={() => openEditCallModal(c)}
                            className="btn-icon"
                            title="Editar llamada"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleToggleCallStatus(c)}
                            className="btn-icon"
                            title={c.status === 'active' ? 'Archivar llamada' : 'Restaurar llamada'}
                          >
                            <Archive size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* TAB 5: HISTORIAL DE CAPTURAS */}
      {/* ==================================================================== */}
      {(canCapture || canStats) && activeTab === 'captures' && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <div>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.2rem 0', color: 'var(--text-main)' }}>
                Historial de Capturas en Campo
              </h2>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Sesiones de entrenamiento y partidos registrados por el equipo (en curso y finalizados).
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="badge badge-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {allCaptures.length} {allCaptures.length === 1 ? 'captura' : 'capturas'}
              </span>
            </div>
          </div>

          {allCaptures.length === 0 ? (
            <div
              className="table-container"
              style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <History size={32} style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }} />
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>No hay capturas registradas para este equipo</p>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem' }}>
                Inicia una sesión de captura desde el botón superior «Captura en Campo» o desde la agenda de concentraciones.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {allCaptures.map((capture) => {
                const isMatch = capture.mode === 'match'
                const isCompleted = capture.status === 'completed'
                const isPaused = capture.status === 'paused'
                const captureDate = new Date(capture.created_at)
                const formattedDate = captureDate.toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                const formattedTime = captureDate.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                })

                return (
                  <div
                    key={capture.id}
                    className="card"
                    style={{
                      padding: '1rem 1.15rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.65rem',
                      borderLeft: isCompleted ? '4px solid #10b981' : '4px solid #f59e0b',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: '0.75rem',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: '240px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                          <h3 style={{ fontSize: '0.98rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                            {capture.title}
                          </h3>

                          {/* Tipo: Entrenamiento o Partido */}
                          <span
                            className={isMatch ? 'badge badge-red' : 'badge badge-info'}
                            style={{ fontSize: '0.7rem' }}
                          >
                            {isMatch ? 'Partido' : 'Entrenamiento'}
                          </span>

                          {/* Estado */}
                          {isCompleted ? (
                            <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>
                              Finalizado
                            </span>
                          ) : isPaused ? (
                            <span className="badge badge-muted" style={{ fontSize: '0.7rem' }}>
                              Pausado
                            </span>
                          ) : (
                            <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>
                              En curso
                            </span>
                          )}

                          {/* Conteo de snaps */}
                          <span
                            className="badge badge-muted"
                            style={{ fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
                          >
                            {capture.snapCount} {capture.snapCount === 1 ? 'snap' : 'snaps'}
                          </span>
                        </div>

                        {/* Fecha y hora */}
                        <div
                          style={{
                            fontSize: '0.78rem',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          <Clock size={13} style={{ color: 'var(--text-muted)' }} />
                          <span>
                            {formattedDate} a las {formattedTime}
                          </span>
                          {capture.ended_at && (
                            <span style={{ color: 'var(--text-muted)' }}>
                              {' · '}Cerrado a las {new Date(capture.ended_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>

                        {/* Si es partido y tiene match_context, mostrar marcador */}
                        {isMatch && capture.match_context && (
                          <div
                            style={{
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              color: 'var(--brand-red)',
                              marginTop: '0.35rem',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            Marcador: España {capture.match_context.our_score} - {capture.match_context.rival_score} vs {capture.match_context.rival_name || 'Rival'}
                            {capture.match_context.period && (
                              <span style={{ fontWeight: 500, color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
                                ({capture.match_context.period})
                              </span>
                            )}
                          </div>
                        )}

                        {/* Alineaciones titulares */}
                        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          Alineación base: {capture.offense_lineup_name} vs {capture.defense_lineup_name}
                        </div>
                      </div>

                      {/* Acción Ver historial */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleOpenHistory(capture)}
                          disabled={loadingHistoryId === capture.id}
                          style={{
                            fontSize: '0.82rem',
                            minHeight: '44px',
                            padding: '0.45rem 0.95rem',
                            backgroundColor: 'var(--brand-red)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            whiteSpace: 'nowrap',
                          }}
                          aria-label={`Ver historial de ${capture.title}`}
                        >
                          <Eye size={15} className={loadingHistoryId === capture.id ? 'spin' : ''} />
                          <span>{loadingHistoryId === capture.id ? 'Cargando snaps...' : 'Ver historial'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* MODAL: SCRIPT EDITOR */}
      {/* ==================================================================== */}
      {scriptModalOpen && (
        <ScriptEditorModal
          isOpen={scriptModalOpen}
          onClose={() => setScriptModalOpen(false)}
          scriptId={selectedScriptId}
          teamId={teamId}
          onSaved={() => {
            loadData()
          }}
        />
      )}

      {/* ==================================================================== */}
      {/* MODAL: LINEUP EDITOR */}
      {/* ==================================================================== */}
      {lineupModalState.isOpen && (
        <LineupEditorModal
          key={`lineup-editor-${lineupModalState.templateId || 'new'}-${lineupModalState.unit}-${lineupModalState.sessionKey || 0}`}
          isOpen={lineupModalState.isOpen}
          onClose={() => setLineupModalState((prev) => ({ ...prev, isOpen: false }))}
          unit={lineupModalState.unit}
          teamId={teamId}
          templateId={lineupModalState.templateId}
          initialName={lineupModalState.initialName}
          initialSlots={lineupModalState.initialSlots}
          athletes={athletes}
          onSaveTemplate={async (name, slots) => {
            await sportsService.saveLineupTemplate({
              templateId: lineupModalState.templateId,
              teamId,
              unit: lineupModalState.unit,
              name,
              slots,
            })
            triggerSuccess('Plantilla de quinteto guardada exitosamente.')
            loadData()
          }}
        />
      )}

      {/* ==================================================================== */}
      {/* MODAL: CREAR / EDITAR JUGADA OFENSIVA */}
      {/* ==================================================================== */}
      <Modal
        isOpen={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
        title={editingPlay ? 'Editar Jugada Ofensiva' : 'Nueva Jugada Ofensiva'}
        subtitle="Define el nombre, código corto y asignación al catálogo táctico"
        size="md"
        hasUnsavedChanges={isPlayDirty}
        footer={
          <>
            <button
              type="button"
              onClick={() => setPlayModalOpen(false)}
              className="btn btn-secondary"
              style={{ fontSize: '0.85rem' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSavePlay}
              className="btn btn-primary"
              style={{ fontSize: '0.85rem' }}
            >
              Guardar Jugada
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Nombre de la Jugada *
            </label>
            <input
              type="text"
              value={playFormData.name}
              onChange={(e) => setPlayFormData({ ...playFormData, name: e.target.value })}
              placeholder="Ej. Flood DEMO, Mesh DEMO..."
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Código Corto *
              </label>
              <input
                type="text"
                value={playFormData.shortCode}
                onChange={(e) => setPlayFormData({ ...playFormData, shortCode: e.target.value.toUpperCase() })}
                placeholder="Ej. BASE-01"
                className="form-input"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Concepto *
              </label>
              <select
                value={playFormData.conceptId}
                onChange={(e) => setPlayFormData({ ...playFormData, conceptId: e.target.value })}
                className="form-input"
                style={{ width: '100%' }}
              >
                {offensiveConcepts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Descripción (opcional)
            </label>
            <textarea
              value={playFormData.description}
              onChange={(e) => setPlayFormData({ ...playFormData, description: e.target.value })}
              placeholder="Descripción general del concepto o lectura principal..."
              rows={2}
              className="form-input"
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Indicaciones / Ajustes de Staff (opcional)
            </label>
            <input
              type="text"
              value={playFormData.coachingNotes}
              onChange={(e) => setPlayFormData({ ...playFormData, coachingNotes: e.target.value })}
              placeholder="Ej. Ajuste vs man coverage, hot route en slot..."
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Modal>

      {/* ==================================================================== */}
      {/* MODAL: CREAR / EDITAR LLAMADA DEFENSIVA */}
      {/* ==================================================================== */}
      <Modal
        isOpen={callModalOpen}
        onClose={() => setCallModalOpen(false)}
        title={editingCall ? 'Editar Llamada Defensiva' : 'Nueva Llamada Defensiva'}
        subtitle="Estructura la cobertura y pautas para el quinteto defensivo"
        size="md"
        hasUnsavedChanges={isCallDirty}
        footer={
          <>
            <button
              type="button"
              onClick={() => setCallModalOpen(false)}
              className="btn btn-secondary"
              style={{ fontSize: '0.85rem' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveCall}
              className="btn btn-primary"
              style={{ fontSize: '0.85rem' }}
            >
              Guardar Llamada
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Nombre de la Llamada *
            </label>
            <input
              type="text"
              value={callFormData.name}
              onChange={(e) => setCallFormData({ ...callFormData, name: e.target.value })}
              placeholder="Ej. Cover 2 Zone DEMO, Cover 1 Press DEMO..."
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Código Corto *
              </label>
              <input
                type="text"
                value={callFormData.shortCode}
                onChange={(e) => setCallFormData({ ...callFormData, shortCode: e.target.value.toUpperCase() })}
                placeholder="Ej. C2-DEMO"
                className="form-input"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Familia / Cobertura *
              </label>
              <input
                type="text"
                value={callFormData.coverageFamily}
                onChange={(e) => setCallFormData({ ...callFormData, coverageFamily: e.target.value })}
                placeholder="Ej. Cover 2, Cover 1, Blitz..."
                className="form-input"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Descripción (opcional)
            </label>
            <textarea
              value={callFormData.description}
              onChange={(e) => setCallFormData({ ...callFormData, description: e.target.value })}
              placeholder="Estructura general de la cobertura o lectura..."
              rows={2}
              className="form-input"
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Ajustes de Staff (opcional)
            </label>
            <input
              type="text"
              value={callFormData.coachingNotes}
              onChange={(e) => setCallFormData({ ...callFormData, coachingNotes: e.target.value })}
              placeholder="Ej. Responsabilidad de rush o cushion..."
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Modal>

      {/* MODAL DE ENTRADA / RESUMEN A CAPTURA EN CAMPO */}
      {canCapture && captureEntryModalOpen && (
        <FieldCaptureEntryModal
          isOpen={captureEntryModalOpen}
          onClose={() => setCaptureEntryModalOpen(false)}
          teamId={teamId}
          initialScriptId={entryScriptId}
          onLaunchCapture={(capture: TeamBlockCapture) => {
            setActiveCaptureId(capture.id)
            setCaptureEntryModalOpen(false)
          }}
        />
      )}

      {/* CONSOLA DE CAPTURA EN CAMPO (MÓVIL 5v5) */}
      {canCapture && activeCaptureId && (
        <FieldConsoleView
          captureId={activeCaptureId}
          onClose={() => {
            setActiveCaptureId(null)
            loadData()
          }}
        />
      )}

      {(canStats || isAthleteUser) && activeTab === 'stats' && (!isMedicUser || isCoachUser) && (
        <SectionErrorBoundary sectionTitle="Estadísticas Deportivas">
          <StatsView teamId={teamId} />
        </SectionErrorBoundary>
      )}

      {/* MODAL DE HISTORIAL CRONOLÓGICO SNAP A SNAP (MODO CONSULTA) */}
      <SnapHistoryModal
        isOpen={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false)
          setSelectedHistoryCapture(null)
          setHistorySnaps([])
        }}
        capture={selectedHistoryCapture}
        snaps={historySnaps}
        onSelectSnap={(snap) => {
          setSelectedDetailSnap(snap)
          setPastSnapDetailOpen(true)
        }}
        rosterAthletes={athletes}
      />

      {/* MODAL DE DETALLE Y AUDITORÍA DE SNAP PASADO */}
      <PastSnapDetailModal
        isOpen={pastSnapDetailOpen}
        onClose={() => {
          setPastSnapDetailOpen(false)
          setSelectedDetailSnap(null)
        }}
        snap={selectedDetailSnap}
        onCorrectResult={async (params) => {
          const updated = await fieldCaptureService.correctSnapResult({
            snapId: params.snapId,
            newPrimaryResult: params.newPrimaryResult,
            passerAthleteId: params.passerId,
            receiverAthleteId: params.receiverId,
            tacklerAthleteId: params.tacklerId,
          })
          setHistorySnaps((prev) => prev.map((s) => (s.id === params.snapId ? updated : s)))
          setSelectedDetailSnap(updated)
        }}
        onVoidSnap={async (snapId, validity, reason) => {
          await fieldCaptureService.voidSnap(snapId, validity, reason)
          setHistorySnaps((prev) =>
            prev.map((s) => (s.id === snapId ? { ...s, validity, review_notes: reason } : s))
          )
        }}
        onUpdateSnapDetails={async (params) => {
          const updated = await fieldCaptureService.updateSnapDetails(params)
          setHistorySnaps((prev) => prev.map((s) => (s.id === params.snapId ? updated : s)))
          setSelectedDetailSnap(updated)
        }}
        rosterAthletes={athletes}
      />
    </div>
  )
}
